import { useEffect, useMemo, useRef, useState } from 'react';
import { Network } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '@/hooks/useCurrency';
import type { ApiError } from '@/services/api';
import {
  supplyAgentService,
  type SupplyAgentFamilyOverviewResponse,
} from '@/services/supplyAgentService';

interface SupplyAgentFamilyOverviewPanelProps {
  warehouseId: number;
  refreshToken?: number;
  /** Reglas efímeras de sesión (solo este pedido) a incluir en el overview. */
  ephemeralRules?: Record<string, unknown>[];
  /** Compacto para embeber en el drawer del agente. */
  embedded?: boolean;
  /** Filtrar ese proveedor en Supply (evitar ir a ciegas). */
  onSelectSupplier?: (supplierId: number, supplierName?: string) => void;
}

function ephemeralRulesKey(rules: Record<string, unknown>[]): string {
  if (rules.length === 0) return '';
  return rules
    .map((rule) => {
      const value = (rule.value as { units?: unknown; amount?: unknown; family_names?: unknown } | undefined) || {};
      const names = Array.isArray(value.family_names)
        ? value.family_names.map(String).join('|')
        : '';
      return `${String(rule.rule_type || '')}:${String(value.units ?? value.amount ?? '')}:${names}`;
    })
    .join(';');
}

function readApiErrorMessage(error: unknown): { status?: number; message: string } {
  if (error && typeof error === 'object' && 'message' in error) {
    const apiError = error as ApiError;
    return {
      status: typeof apiError.status === 'number' ? apiError.status : undefined,
      message: String(apiError.message || ''),
    };
  }
  if (error instanceof Error) {
    return { message: error.message };
  }
  return { message: '' };
}

export function SupplyAgentFamilyOverviewPanel({
  warehouseId,
  refreshToken = 0,
  ephemeralRules = [],
  embedded = false,
  onSelectSupplier,
}: SupplyAgentFamilyOverviewPanelProps) {
  const { t, i18n } = useTranslation();
  const { formatRounded } = useCurrency();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [overview, setOverview] = useState<SupplyAgentFamilyOverviewResponse | null>(null);
  const ephemeralKey = useMemo(() => ephemeralRulesKey(ephemeralRules), [ephemeralRules]);
  const ephemeralRulesRef = useRef(ephemeralRules);
  ephemeralRulesRef.current = ephemeralRules;

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      setForbidden(false);
      try {
        const rules = ephemeralRulesRef.current;
        const data = await supplyAgentService.familyOverview(
          warehouseId,
          rules.length > 0 ? rules : undefined,
        );
        if (cancelled) return;
        setOverview(data);
      } catch (e) {
        if (cancelled) return;
        const { status, message } = readApiErrorMessage(e);
        // 403: UI local con VITE_SUPPLY_AGENT_ENABLED sin flag server-side.
        // No mostrar alerta engañosa de «simulación».
        if (status === 403) {
          setForbidden(true);
          setOverview(null);
          setError(null);
          return;
        }
        setError(message || t('supply.supplyAgent.familyOverviewError'));
        setOverview(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [warehouseId, refreshToken, ephemeralKey, t]);

  if (forbidden) {
    return null;
  }

  if (loading && !overview) {
    return null;
  }

  if (error) {
    return (
      <p className={`text-xs text-red-600 ${embedded ? '' : 'mb-3'}`} role="alert">
        {error}
      </p>
    );
  }

  const families = overview?.families || [];
  if (families.length === 0) {
    return null;
  }

  const fmtU = (n: number) => n.toLocaleString(i18n.language);
  const fmtE = (n: number) => formatRounded(n);

  return (
    <div
      className={`rounded-lg border border-amber-200 bg-amber-50/70 px-3 py-2 shadow-sm ${
        embedded ? '' : 'mb-3'
      }`}
    >
      <h4 className="text-sm font-medium text-amber-950 flex items-center gap-1.5 mb-1">
        <Network className="w-4 h-4" />
        {t('supply.supplyAgent.familyOverviewTitle')}
      </h4>
      <p className="text-[11px] text-amber-900/80 mb-2">
        {onSelectSupplier
          ? t('supply.supplyAgent.familyOverviewHintClick')
          : t('supply.supplyAgent.familyOverviewHint')}
      </p>
      <ul className="space-y-2">
        {families.map((family) => {
          const names = (family.family_names || []).join(', ') || '—';
          const global = Number(family.global_units || 0);
          const threshold =
            family.threshold != null
              ? Number(family.threshold)
              : family.cap != null
                ? Number(family.cap)
                : null;
          const isMin = family.rule_type === 'family_minimum_quantity';
          const isMinValue = family.rule_type === 'family_minimum_order_value';
          const isMax = family.rule_type === 'family_maximum_quantity';
          const globalDisplay = isMinValue
            ? fmtE(Number(family.global_value ?? global))
            : fmtU(global);
          const thresholdDisplay =
            threshold != null ? (isMinValue ? fmtE(threshold) : fmtU(threshold)) : '—';
          const status = family.status || 'ok';
          const gap = Number(family.gap_to_min || 0);
          const gapDisplay = isMinValue ? fmtE(gap) : fmtU(gap);
          const excess = Number(family.excess_over_max || 0);
          const bySupplier = family.by_supplier || [];

          return (
            <li
              key={String(family.rule_id)}
              className="rounded-md border border-amber-100 bg-white/80 px-2 py-1.5 text-xs text-amber-950"
            >
              <p className="font-medium">
                {isMin || isMinValue
                  ? t('supply.supplyAgent.familyOverviewMinRow', {
                      family: names,
                      global: globalDisplay,
                      min: thresholdDisplay,
                    })
                  : isMax
                    ? t('supply.supplyAgent.familyOverviewMaxRow', {
                        family: names,
                        global: fmtU(global),
                        max: threshold != null ? fmtU(threshold) : '—',
                      })
                    : t('supply.supplyAgent.familyOverviewRow', {
                        family: names,
                        global: globalDisplay,
                        cap: thresholdDisplay,
                      })}
              </p>
              <p
                className={`mt-0.5 text-[11px] font-medium ${
                  status === 'ok' ? 'text-emerald-800' : 'text-amber-900'
                }`}
              >
                {status === 'below_min'
                  ? t('supply.supplyAgent.familyStatusBelowMin', { gap: gapDisplay })
                  : status === 'over_max'
                    ? t('supply.supplyAgent.familyStatusOverMax', { excess: fmtU(excess) })
                    : isMin || isMinValue
                      ? t('supply.supplyAgent.familyStatusMinOk')
                      : t('supply.supplyAgent.familyStatusMaxOk')}
              </p>
              {bySupplier.length > 0 && (
                <ul className="mt-1.5 space-y-0.5">
                  {bySupplier.map((row) => {
                    const units = Number(row.units || 0);
                    const label = `${row.supplier_name}: ${fmtU(units)} ${t(
                      'supply.supplyAgent.units',
                    )}`;
                    if (!onSelectSupplier) {
                      return (
                        <li key={row.supplier_id} className="text-[11px] text-amber-900/80">
                          {label}
                        </li>
                      );
                    }
                    return (
                      <li key={row.supplier_id}>
                        <button
                          type="button"
                          onClick={() => onSelectSupplier(row.supplier_id, row.supplier_name)}
                          className="w-full rounded px-1 py-0.5 text-left text-[11px] text-sky-800 hover:bg-sky-50 hover:text-sky-950"
                          title={t('supply.supplyAgent.familySelectSupplier')}
                        >
                          {label}
                          <span className="ml-1 text-sky-600/80">
                            · {t('supply.supplyAgent.familySelectSupplierShort')}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
              {bySupplier.length === 0 && (
                <p className="mt-0.5 text-[11px] text-amber-900/70">
                  {t('supply.supplyAgent.familyOverviewEmptySuppliers')}
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
